import React, { useRef, useState } from "react";
import { Download, FileCheck, Link2, Loader2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { showStatusToast } from "../../components/ui/Toast";
import { telechargerFecExcel } from "./fecExcel";

export default function FecUploadZone({
  label,
  period,
  file,
  onFileChange,
  icon: Icon,
  chargement = false,
}) {
  const inputRef = useRef(null);
  const [telechargement, setTelechargement] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile)) onFileChange(droppedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && isValidFile(selectedFile)) onFileChange(selectedFile);
  };

  const isValidFile = (f) =>
    ["txt", "csv", "pdf"].includes(f.name.split(".").pop().toLowerCase());

  // `file` est un File (dépôt manuel) ou un FEC récupéré depuis ACD
  // ({ acd: true, nom, url, exercice: { debut, fin }, lignes }).
  const depuisAcd = !!file?.acd;
  const dateFr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

  const handleRemove = (e) => {
    e.stopPropagation();
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  // Téléchargement du FEC récupéré depuis ACD, converti en Excel lisible
  // (SIRENFECAAAAMMJJ.xlsx) ; spinner jusqu'à la fin de la conversion.
  const handleDownload = async (e) => {
    e.stopPropagation();
    if (telechargement) return;
    setTelechargement(true);
    try {
      await telechargerFecExcel(file, label);
    } catch (err) {
      showStatusToast({
        status: "error",
        titre: "Téléchargement impossible",
        message: `Le ${label} n'a pas pu être converti en Excel${err?.message ? ` : ${err.message}` : "."}`,
      });
    } finally {
      setTelechargement(false);
    }
  };

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onClick={() => !file && !chargement && inputRef.current?.click()}
      aria-busy={chargement || undefined}
      className={`relative flex-1  min-w-0 rounded-lg border-2 border-dashed p-6 transition-all duration-200 ${
        chargement
          ? "border-[#840040]/20 bg-[#840040]/[0.02] cursor-wait"
          : file
            ? "border-emerald-300 bg-emerald-50/50"
            : "border-border hover:border-[#840040]/40 hover:bg-[#840040]/[0.02] cursor-pointer"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.csv, .pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {depuisAcd && !chargement && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={telechargement}
          aria-busy={telechargement || undefined}
          title={telechargement ? "Préparation du fichier Excel…" : `Télécharger en Excel (${file.nom.replace(/\.txt$/i, ".xlsx")})`}
          aria-label={telechargement ? `${label} : préparation du fichier Excel` : `Télécharger le ${label} en Excel`}
          className="absolute left-2 top-2 rounded-md p-1.5 text-emerald-700 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-wait disabled:hover:bg-transparent"
        >
          {telechargement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
      )}

      <AnimatePresence mode="wait">
        {chargement ? (
          // Récupération depuis ACD en cours : squelette à la forme du
          // contenu chargé (pulsation en opacité, sans mouvement).
          <motion.div
            key="chargement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3 animate-[pulse_1.2s_ease-in-out_infinite]"
            aria-label={`${label} : récupération depuis ACD`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#840040]/10" />
            <div className="flex flex-col items-center gap-1.5">
              <span className="block h-3 w-28 rounded bg-slate-300/70" />
              <span className="block h-2.5 w-36 rounded bg-slate-300/70" />
            </div>
            <span className="block h-2.5 w-16 rounded bg-slate-300/70" />
          </motion.div>
        ) : !file ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center text-center gap-3"
          >
            <div className="w-12 h-12 rounded-xl bg-[#840040]/5 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#840040]/60" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{period}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Glissez-déposez ou cliquez •{" "}
              <span className="font-medium">.txt .csv .pdf</span>
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="loaded"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex flex-col items-center text-center gap-3"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              {depuisAcd ? <Link2 className="w-5 h-5 text-emerald-600" /> : <FileCheck className="w-5 h-5 text-emerald-600" />}
            </div>
            <div className="min-w-0 max-w-full">
              <p className="text-xs font-semibold text-foreground truncate max-w-[180px]" title={depuisAcd ? file.nom : file.name}>
                {depuisAcd ? `${label} ${file.exercice.fin.slice(0, 4)}` : file.name}
              </p>
              <p className="text-xs text-emerald-600 font-medium mt-0.5">
                {depuisAcd ? "Récupéré depuis ACD ✓" : "Fichier chargé ✓"}
              </p>
              {depuisAcd && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Du {dateFr(file.exercice.debut)} au {dateFr(file.exercice.fin)} ·{" "}
                  {file.lignes.toLocaleString("fr-FR")} lignes d'écritures
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors"
            >
              <X className="w-3 h-3" />
              Retirer
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
